/* Copyright (c) 2020-present CloudIO Inc */

import { buttonSize } from 'componentregistry/registry/constants';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './StarColumn';
import { helpRenders } from './StarColumn_Help';

const StarColumnManager: ComponentManagerFn<'StarColumn'> = (
  platform: Platform,
): ComponentManager<'StarColumn'> => {
  return {
    ...baseComponentManager<'StarColumn'>(platform, 'StarColumn'),
    type: 'StarColumn',
    name: 'Rating Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnCellClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }) => {
          return ['Integer', 'Decimal', 'Double'];
        },
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
      },
      numberOfStars: {
        label: 'Max Rating',
        type: 'number',
        min: 3,
        max: 10,
      },
      precision: {
        label: 'Allow Semi Rating',
        type: 'boolean',
        helpText:
          'Check to allow the rating with increments of 0.5 (half star) instead of a full star.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      return true;
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    replaceableWith: ['NumberColumn', 'ProgressIndicatorColumn'],
    category: 'Data Grid',
    icon: { iconName: 'star' },
    description: `**Overview**<br/>
The Rating Column visualizes numeric data as a set of stars.
Instead of displaying a raw number like 4.5 or 3, it renders a familiar 5-star (or custom) rating interface. It is commonly used for Customer Feedback, Review Scores, Skills Assessments, or Priority Levels directly within a Data Grid.

**1. Data Binding**<br/>
To function, this column must bind to a numeric field in your Data Store.
* **View Attribute:** Select the Data Store field.
* **Supported Types:** Integer, Decimal, Double.
* **Logic:** The numeric value (e.g., 3) determines how many stars are filled (e.g., 3 filled stars).

**2. Configuration & Style**<br/>
You can customize the rating scale and visual appearance.
* **Max Rating (Number of Stars):** Sets the maximum value/count of stars displayed. (Range: Min 3, Max 10. Default: 5 stars).
* **Allow Semi Rating (Precision):**
    * **Enabled:** Allows half-star ratings (e.g., 3.5). Users can click halfway through a star to set this value.
    * **Disabled:** Restricts input to whole numbers only (e.g., 3 or 4).
* **Color:** Sets the theme color of the filled stars (e.g., Primary, Secondary, or Warning for gold/yellow).
* **Size:** Controls the size of the stars (Small, Medium, Large).

**3. Interaction**<br/>
* **Read-Only Mode:** If the column is set to **Updatable: False**, users can see the rating but cannot change it.
* **Editing:** If editable, users can click on the stars to update the underlying numeric value instantly.
* **TriggerOnCellClick:** You can add this child component to execute logic when a user clicks to rate. (Use Case: "Thank you for your rating" popup or logging the interaction timestamp).

**4. Best Practices**<br/>
* **Sorting:** Since the underlying data is a standard number, sorting works naturally (High to Low ratings).
* **Context:** For precision-critical reports, consider placing a read-only **Number Column** next to the stars to show the exact value (e.g., 4.25).`,
    helpRenders,
  };
};

export default StarColumnManager;
